import React from 'react';
import { useImmerReducer } from 'use-immer';
import { RADIOS_INITIAL_VALUES } from '../lib/buildInitialState';
import { Box, HStack, Flex, Switch, Text, Textarea, Button, Portal, Dialog, CloseButton } from '@chakra-ui/react';
import theme from '../theme';
import SignaturePad from '../components/SignaturePad';

const getCrewMembers = (
	{ employee_crew_members = [], non_employee_crew_members = [], crew_members = [], crew = [] },
	supervisor = false
) =>
	[...employee_crew_members, ...non_employee_crew_members, ...crew_members]
		.filter(
			cm =>
				cm.employee_crew_member_name ||
				cm.non_employee_crew_member_name ||
				cm.crew_member_name ||
				cm.employee_crew_member_role ||
				cm.non_employee_crew_member_role ||
				cm.crew_member_role
		)
		.map(cm => {
			cm.name = cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name;
			cm.role = cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role;

			const prevCm = crew.find(({ name }) => name.toLowerCase() === cm.name.toLowerCase());

			if (prevCm) {
				cm.signature = prevCm.signature ?? null;
			}

			return { ...cm };
		})
		.filter(({ role }) => !supervisor || role.includes('supervisor'));

function reducer(draft, action) {
	switch (action.type) {
		case 'update_crew_members': {
			const { crew } = action.payload;

			draft.fields.crew = crew;

			break;
		}
		case 'update_field_value': {
			const { field, value, key } = action.payload;

			if (key) {
				draft.fields[field] = {
					...draft.fields[field],
					...RADIOS_INITIAL_VALUES,
					[key]: value
				};
			} else {
				draft.fields[field] = value;
			}

			break;
		}
		case 'save_signature': {
			const { cmIndex, signatureData } = action.payload;

			const crewMember = draft.fields.crew[cmIndex];

			if (crewMember) {
				crewMember.signature = signatureData;
			}

			break;
		}
		default:
			break;
	}
}

export default ({ formData, currentForm, handoffData, dataAPI, edit, saveForm, isSubmitted }) => {
	const deltaPData = React.useMemo(() => dataAPI.getFormData('delta_p_list'), [dataAPI]);

	const [state, dispatch] = useImmerReducer(reducer, {
		fields: {
			...formData,
			crew: getCrewMembers({ ...handoffData, crew: formData?.crew }),
			potential_hazards: formData?.potential_hazards || deltaPData?.dive_planning?.potential_hazards || '',
			emergency_assets: formData?.emergency_assets || deltaPData?.mitigation_plan?.emergency_assets || '',
			emergency_procedures:
				formData?.emergency_procedures || deltaPData?.mitigation_plan?.emergency_procedures || ''
		}
	});

	const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
	const [supervisorDialogOpen, setSupervisorDialogOpen] = React.useState(false);

	const signatureRef = React.useRef();

	const supervisor = React.useMemo(
		() => state.fields?.crew?.find(({ role }) => role.includes('supervisor')),
		[state.fields?.crew]
	);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	return (
		<>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Flex flexDirection="row" alignItems="center">
						<Flex px="8px" justifyContent="center" alignItems="center">
							<Flex
								justifyContent="center"
								alignItems="center"
								w="24px"
								h="24px"
								borderWidth="2px"
								borderRadius="50%"
								// Null propagation for retroactively added fields
								borderColor={state.fields?.delta_p_hazards?.yes ? 'green' : 'transparent'}
								_hover={{ cursor: isSubmitted && !edit ? 'default' : 'pointer' }}
								pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
								onClick={() => {
									if (!(isSubmitted && !edit)) {
										if (state.fields?.delta_p_hazards?.no) {
											dispatch({
												type: 'update_crew_members',
												payload: {
													crew: getCrewMembers({ ...handoffData, crew: state.fields?.crew })
												}
											});
										}

										dispatch({
											type: 'update_field_value',
											payload: {
												field: 'delta_p_hazards',
												key: 'yes',
												value: !state.fields?.delta_p_hazards?.yes
											}
										});
									}
								}}
							>
								<Text fontSize={18} w="17.5px" align="center">
									Y
								</Text>
							</Flex>
						</Flex>
						<Flex px="8px" justifyContent="center" alignItems="center">
							<Dialog.Root
								role="alertdialog"
								initialFocusEl={signatureRef}
								onOpenChange={({ open }) => {
									if (!open) {
										setSignatureDialogOpen(false);
									}
								}}
								open={signatureDialogOpen}
								placement="center"
							>
								<Portal>
									<Dialog.Backdrop />
									<Dialog.Positioner>
										<Dialog.Content opacity="1 !important">
											<Dialog.Header>
												<Dialog.Title fontSize="lg" fontWeight="bold">
													Sign Below
												</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<p>Remember to have the supervisor sign below.</p>
											</Dialog.Body>
											<Dialog.Footer>
												<Dialog.ActionTrigger asChild>
													<Button
														ref={signatureRef}
														onClick={() => setSignatureDialogOpen(false)}
													>
														Okay
													</Button>
												</Dialog.ActionTrigger>
											</Dialog.Footer>
											<Dialog.CloseTrigger asChild>
												<CloseButton size="sm" />
											</Dialog.CloseTrigger>
										</Dialog.Content>
									</Dialog.Positioner>
								</Portal>
							</Dialog.Root>
							<Dialog.Root
								role="alertdialog"
								initialFocusEl={signatureRef}
								onOpenChange={({ open }) => {
									if (!open) {
										setSupervisorDialogOpen(false);
									}
								}}
								open={supervisorDialogOpen}
								placement="center"
							>
								<Portal>
									<Dialog.Backdrop />
									<Dialog.Positioner>
										<Dialog.Content opacity="1 !important">
											<Dialog.Header>
												<Dialog.Title fontSize="lg" fontWeight="bold">
													Supervisor Missing
												</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<p>
													Please ensure you have at least one Supervisor in your team members.
												</p>
											</Dialog.Body>
											<Dialog.Footer>
												<Dialog.ActionTrigger asChild>
													<Button
														ref={signatureRef}
														onClick={() => setSupervisorDialogOpen(false)}
													>
														Okay
													</Button>
												</Dialog.ActionTrigger>
											</Dialog.Footer>
											<Dialog.CloseTrigger asChild>
												<CloseButton size="sm" />
											</Dialog.CloseTrigger>
										</Dialog.Content>
									</Dialog.Positioner>
								</Portal>
							</Dialog.Root>
							<Flex
								justifyContent="center"
								alignItems="center"
								w="24px"
								h="24px"
								borderWidth="2px"
								borderRadius="50%"
								borderColor={state.fields?.delta_p_hazards?.no ? 'red' : 'transparent'}
								_hover={{ cursor: isSubmitted && !edit ? 'default' : 'pointer' }}
								pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
								onClick={() => {
									if (!(isSubmitted && !edit)) {
										if (supervisor) {
											if (!state.fields?.delta_p_hazards?.no) {
												dispatch({
													type: 'update_field_value',
													payload: {
														field: 'delta_p_hazards_acknowledged',
														value: false
													}
												});
											}

											dispatch({
												type: 'update_crew_members',
												payload: {
													crew: getCrewMembers(
														{ ...handoffData, crew: state.fields?.crew },
														!state.fields?.delta_p_hazards?.no
													)
												}
											});

											dispatch({
												type: 'update_field_value',
												payload: {
													field: 'delta_p_hazards',
													key: 'no',
													value: !state.fields?.delta_p_hazards?.no
												}
											});
										} else {
											setSupervisorDialogOpen(true);
										}
									}
								}}
							>
								<Text fontSize={18} w="17.5px" align="center">
									N
								</Text>
							</Flex>
						</Flex>
					</Flex>
					<Text fontSize={16} as="label" pl="4px">
						Are there any Delta-P Hazards on this jobsite today?
					</Text>
				</HStack>
			</Flex>
			{supervisor && state.fields?.delta_p_hazards?.no ? (
				<Flex ml="60px">
					<HStack
						w="100%"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
						borderBottom={`2px solid ${
							!state.fields?.delta_p_hazards_acknowledged ? 'red' : 'transparent'
						}`}
					>
						<Switch.Root
							colorPalette="blue"
							size="lg"
							checked={state.fields?.delta_p_hazards_acknowledged}
							disabled={isSubmitted && !edit}
							onCheckedChange={() => {
								if (!state.fields?.delta_p_hazards_acknowledged) {
									setSignatureDialogOpen(true);
								}

								dispatch({
									type: 'update_field_value',
									payload: {
										field: 'delta_p_hazards_acknowledged',
										value: !state.fields?.delta_p_hazards_acknowledged
									}
								});
							}}
						>
							<Switch.HiddenInput />
							<Switch.Control />
						</Switch.Root>
						<Text fontSize={16} as="label" pl="4px">
							I acknowledge that differential pressure hazards were discussed in our team meeting today
							and that there are no Delta-P hazards present onsite.
						</Text>
					</HStack>
				</Flex>
			) : null}
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Potential Hazards
					</Text>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields?.potential_hazards}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'potential_hazards',
									value: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Box>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'delta_p_hazards_located',
									value: !state.fields?.delta_p_hazards_located
								}
							})
						}
						checked={state.fields?.delta_p_hazards_located}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Along with site representatives, all members of the dive team must locate{' '}
						<Text as="u">all</Text> Delta-P hazards. Document any changes above.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'work_conducted_delta_p_eliminated',
									value: !state.fields?.work_conducted_delta_p_eliminated
								}
							})
						}
						checked={state.fields?.work_conducted_delta_p_eliminated}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Verify the work is being conducted when the maximum number of Delta-P sources can be eliminated
						(pumps secured, valves isolated).
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'structure_layout_delta_p',
									value: !state.fields?.structure_layout_delta_p
								}
							})
						}
						checked={state.fields?.structure_layout_delta_p}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						All dive team members understand the layout of the structure and areas where Delta-P may be
						present.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'capture_zone_reviewed',
									value: !state.fields?.capture_zone_reviewed
								}
							})
						}
						checked={state.fields?.capture_zone_reviewed}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Review the capture zone of the known hazards and the minimum safe distance the diver and
						umbilical need to maintain.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'established_location_reviewed',
									value: !state.fields?.established_location_reviewed
								}
							})
						}
						checked={state.fields?.established_location_reviewed}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Review the established best location to tend the diver that minimizes exposure.
					</Text>
				</HStack>
			</Flex>
			<Flex ml="60px">
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Flex flexDirection="row" alignItems="center">
						<Flex px="8px" justifyContent="center" alignItems="center">
							<Flex
								justifyContent="center"
								alignItems="center"
								w="24px"
								h="24px"
								borderWidth="2px"
								borderRadius="50%"
								borderColor={state.fields?.original_plan_modified?.yes ? 'green' : 'transparent'}
								_hover={{
									cursor: isSubmitted && !edit ? 'default' : 'pointer'
								}}
								pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
								onClick={() => {
									if (!(isSubmitted && !edit)) {
										dispatch({
											type: 'update_field_value',
											payload: {
												field: 'original_plan_modified',
												key: 'yes',
												value: !state.fields?.original_plan_modified?.yes
											}
										});
									}
								}}
							>
								<Text fontSize={18} w="17.5px" align="center">
									Y
								</Text>
							</Flex>
						</Flex>
						<Flex px="8px" justifyContent="center" alignItems="center">
							<Flex
								justifyContent="center"
								alignItems="center"
								w="24px"
								h="24px"
								borderWidth="2px"
								borderRadius="50%"
								borderColor={state.fields?.original_plan_modified?.no ? 'red' : 'transparent'}
								_hover={{
									cursor: isSubmitted && !edit ? 'default' : 'pointer'
								}}
								pointerEvents={isSubmitted && !edit ? 'none' : 'auto'}
								onClick={() => {
									if (!(isSubmitted && !edit)) {
										dispatch({
											type: 'update_field_value',
											payload: {
												field: 'original_plan_modified',
												key: 'no',
												value: !state.fields?.original_plan_modified?.no
											}
										});
									}
								}}
							>
								<Text fontSize={18} w="17.5px" align="center">
									N
								</Text>
							</Flex>
						</Flex>
					</Flex>
					<Text fontSize={16} as="label" pl="4px">
						Was this modified from the original plan?
					</Text>
				</HStack>
			</Flex>
			<Flex ml="60px">
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Detail why the location was changed.
					</Text>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields?.location_changed_reason}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'location_changed_reason',
									value: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Box>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'lockout_tagout_plan_implemented',
									value: !state.fields?.lockout_tagout_plan_implemented
								}
							})
						}
						checked={state.fields?.lockout_tagout_plan_implemented}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Has the site-specific lockout/tagout plan been implemented?
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'lockout_tagout_plan_reviewed',
									value: !state.fields?.lockout_tagout_plan_reviewed
								}
							})
						}
						checked={state.fields?.lockout_tagout_plan_reviewed}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Review site-specific lockout/tagout plan with team.
					</Text>
				</HStack>
			</Flex>
			<Flex ml="60px">
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'physical_barriers_verified',
									value: !state.fields?.physical_barriers_verified
								}
							})
						}
						checked={state.fields?.physical_barriers_verified}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						If physical barriers were planned, are they verified in place?
					</Text>
				</HStack>
			</Flex>
			<Flex ml="60px">
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onCheckedChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'no_flow_conditions',
									value: !state.fields?.no_flow_conditions
								}
							})
						}
						checked={state.fields?.no_flow_conditions}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Have no-flow conditions been met and verified with the use of tell-tales, flowmeters, ROV, sonar
						imaging, or visual observations downstream of water flow?
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'conditions_diver_plan',
									value: !state.fields?.conditions_diver_plan
								}
							})
						}
						checked={state.fields?.conditions_diver_plan}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						If conditions are not as planned or change during the dive, the diver will be brought to the
						surface and the plan reevaluated.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'diver_communicate_monitored_tracked',
									value: !state.fields?.diver_communicate_monitored_tracked
								}
							})
						}
						checked={state.fields?.diver_communicate_monitored_tracked}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						Divers will continuously communicate all movements using three-way communication with topside
						and be tracked by monitoring umbilical length and communication with the diver and/or using
						sonar imaging.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'diver_tended',
									value: !state.fields?.diver_tended
								}
							})
						}
						checked={state.fields?.diver_tended}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						The diver will be tended at all times. Diver will verbally ask for slack (or ask for slack to be
						taken up) using three-way communication.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Switch.Root
						colorPalette="blue"
						size="lg"
						onChange={() =>
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'water_flow_report_change',
									value: !state.fields?.water_flow_report_change
								}
							})
						}
						checked={state.fields?.water_flow_report_change}
						disabled={isSubmitted && !edit}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text fontSize={16} as="label" pl="4px">
						When the work site is reached, the diver will report the water flow to the supervisor and report
						any change in water flow immediately.
					</Text>
				</HStack>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Emergency rescue/recovery assets
					</Text>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields?.emergency_assets}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'emergency_assets',
									value: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Box>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text fontSize={16} as="label" pb="4px">
						Emergency procedures for diver recovery
					</Text>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields?.emergency_procedures}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'emergency_procedures',
									value: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Box>
			</Flex>
			{state.fields?.crew?.map((cm, cmIndex) => (
				<Flex key={`${cm.name}_${cmIndex}`}>
					<Flex w="100%" align="flex-start">
						<Box flex="2" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
							<Text fontSize={16} as="label" pb="4px">
								Name
							</Text>
							<Text fontSize={16} h="38px">
								{cm.name}
							</Text>
						</Box>
					</Flex>
					<Flex
						w="100%"
						m="8px"
						borderBottomWidth="2px"
						borderBottomColor={cm.signature ? 'transparent' : 'red'}
						wrap="wrap"
						alignItems="flex-end"
					>
						<SignaturePad
							disabled={isSubmitted && !edit}
							src={
								cm.signature ||
								'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
							}
							onSign={signatureData =>
								dispatch({
									type: 'save_signature',
									payload: {
										name: cm.name,
										cmIndex,
										signatureData
									}
								})
							}
						/>
					</Flex>
					<Flex minWidth="56px" />
				</Flex>
			))}
		</>
	);
};
